import type { InjectionKey, Ref, ShallowRef } from 'vue';

/** Which of the two panels is shown: both, or only one of them. */
type NxSplitterPanelState = 'split' | 'only_first' | 'only_second';

/** The panel of a group that carries an explicit size while the other flexes. */
type NxSplitterSizedPanel = 'first' | 'second';

type PanelData = {
  isShown: Ref<boolean | undefined>;
  /** Main-axis size (px) of the sized panel (unset on the other); name kept for API compatibility. */
  heightOverwrite: Ref<number | undefined>;
  panelElement: Readonly<ShallowRef<HTMLDivElement | null>>;
  /** Smallest main-axis size (px) of the panel, read live from its props. */
  minSize?: () => number | undefined;
  /** Largest main-axis size (px) of the panel, read live from its props. */
  maxSize?: () => number | undefined;
};

/** The divider as a keyboard-operable separator (WAI-ARIA window splitter). */
type SplitterSeparator = {
  /** Moves the divider by `delta` px (positive = towards the second panel), as a drag would. */
  resizeBy: (delta: number) => void;
  /** Home / End: the divider to the start or the end of the range the limits leave. */
  resizeToEdge: (edge: 'min' | 'max') => void;
  /** Divider position in percent of the space both panels share, with its range. */
  value: Readonly<Ref<{ now: number; min: number; max: number }>>;
  /** Id of the sized panel, the one the separator sizes (`aria-controls`). */
  controls: () => string | undefined;
};

/** The internal `splitterInjectionKey` is used by the `NxSplitterPanel` component, the `NxSplitterResizeHandle` component and is provided by the `NxSplitterGroup` component */
const splitterInjectionKey = Symbol() as InjectionKey<{
  /** Adds the panel with the given data to the splitter group. */
  addPanel: (panelData: PanelData) => void;
  /** Starts dragging of the resize handle. */
  startDragging: (payload: MouseEvent) => void;
  /** Whether the button down (used to expand the first panel) should be shown. */
  showButtonDown: Readonly<Ref<boolean>>;
  /** Triggers the first panel to expand. */
  clickButtonDown: (payload: PointerEvent) => void;
  /** Whether the button up (used to expand the second panel) should be shown. */
  showButtonUp: Readonly<Ref<boolean>>;
  /** Triggers the second panel to expand. */
  clickButtonUp: (payload: PointerEvent) => void;
  /** Sets the actual resize handle client height on mount. The splitter group assumes 38 until then. */
  setResizeHandleHeight: (resizeHandleElementClientHeight: number) => void;
  /**
   * Whether the divider is a vertical line (panes side by side, flex row).
   * Mirrors `orientation === 'vertical'` on NxSplitterGroup.
   */
  isVertical: boolean;
  /** Keyboard resizing; optional, so a handle given a bare injection still renders. */
  separator?: SplitterSeparator;
}>;

export { splitterInjectionKey };
export type { NxSplitterPanelState, NxSplitterSizedPanel, PanelData, SplitterSeparator };
